import { Component, inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

/**
 * AdvanceInvoiceNumberDialogData is what the caller has to show the
 * operator before the renumbering: the number the invoice is leaving and
 * the one it is about to get. `nextNumber` is computed by the caller for
 * display only — the server mints the authoritative number and refuses
 * the call outright when the ordinal is exhausted, the stored number was
 * hand-edited, or the invoice has reached accounting.
 */
export interface AdvanceInvoiceNumberDialogData {
  currentNumber: number;
  nextNumber: number;
}

/**
 * AdvanceInvoiceNumberDialogComponent confirms an Advance Invoice Number
 * and collects the mandatory reason for it (design D9). It closes with the
 * trimmed reason on confirm and with `undefined` on cancel, escape or a
 * backdrop click, so the caller can treat "a string came back" as consent.
 *
 * The shared ConfirmationDialogComponent is not used because it has no
 * input and its `confirm()` resolves to a boolean; extending it would
 * touch every caller for one field (design D8).
 *
 * The disabled confirm button is a convenience only: the server trims the
 * reason too and refuses a blank one with `422 reason_required`, exactly
 * as the invoice state reset does.
 */
@Component({
  selector: 'app-advance-invoice-number-dialog',
  templateUrl: './advance-invoice-number-dialog.component.html',
  styleUrls: ['./advance-invoice-number-dialog.component.scss'],
})
export class AdvanceInvoiceNumberDialogComponent {
  public readonly dialogRef =
    inject<MatDialogRef<AdvanceInvoiceNumberDialogComponent, string>>(MatDialogRef);
  public readonly data = inject<AdvanceInvoiceNumberDialogData>(MAT_DIALOG_DATA);

  public readonly reasonControl = new UntypedFormControl('');

  // The trimmed reason is both what gates the confirm button and what the
  // dialog hands back, so whitespace alone can never be consent.
  public get reason(): string {
    const value = this.reasonControl.value as string | null;
    return (value ?? '').trim();
  }

  public onConfirm(): void {
    const reason = this.reason;
    if (!reason) {
      return;
    }
    this.dialogRef.close(reason);
  }

  public onCancel(): void {
    this.dialogRef.close(undefined);
  }
}
